
import {computed, defineComponent, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {
	availableTime,
	currentAttempt,
	currentGame,
	currentRound,
	currentTrack,
	firstRound,
	markStart,
	nextAttemptTime,
	playerCorrect,
	playerCorrectTime,
	playerGuesses,
	roundFinished,
	showShareScreen,
	submitGuess,
	totalRounds,
} from '@/state/game';
import {GuessType, guessTypeInfo, secondSteps, totalSeconds} from '@/data/constants';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';
import autoComplete from '@tarekraafat/autocomplete.js';
import {dropdownList} from '@/data/music';
import {
	audioCurrentPosition,
	audioDuration,
	audioLimit,
	audioPlaying,
	playAudio,
	setAudio,
	stopAudio,
} from '@/state/audio';

interface GuessItem {
	class: string;
	icon?: string;
	answer?: string | null;
}

export default defineComponent({
	components: {FontAwesomeIcon},
	setup() {
		const guessList = computed<GuessItem[]>(() => {
			return secondSteps.map((_, i) => {
				const playerGuess = playerGuesses.value[i];
				const info = guessTypeInfo[playerGuess?.type];
				return {
					class: (info && info[0]) ?? (currentAttempt.value === i ? 'active' : 'none'),
					icon: (info && info[1]),
					answer: playerGuess?.type === GuessType.SKIP ? 'Skipped' : playerGuess?.answer,
				};
			});
		});

		let autoCompleteJS: any;

		onMounted(() => {
			autoCompleteJS = new autoComplete({
				selector: '#autocomplete',
				searchEngine: 'loose',
				diacritics: true,
				data: {
					src: dropdownList,
					keys: ['title'],
				},
				resultsList: {
					class: 'autocomplete-list',
					maxResults: 10,
				},
				resultItem: {
					highlight: true,
					selected: 'selected',
				},
			});
			setAudio(`audio/${currentTrack.value.audio}`);
			updateLimit();
		});

		watch(currentTrack, () => {
			setAudio(`audio/${currentTrack.value.audio}`);
		});

		onBeforeUnmount(() => {
			autoCompleteJS.unInit();
			stopAudio();
		});

		const formField = ref<HTMLInputElement>();

		const displayTime = (time: number) => {
			const minutes = Math.floor(time / 60);
			const seconds = Math.floor(time % 60);
			return `${minutes}:${seconds.toString().padStart(2, '0')}`;
		};

		const currentTimeDisplay = computed(() => displayTime(audioCurrentPosition.value));
		const endTime = computed(() => roundFinished.value ? audioDuration.value : totalSeconds);
		const endTimeDisplay = computed(() => displayTime(endTime.value));

		const togglePlay = () => {
			if (audioPlaying.value) stopAudio();
			else playAudio();
			markStart();
		};

		const updateLimit = () => {
			audioLimit.value = roundFinished.value ? 0 : availableTime.value;
		};
		watch(availableTime, updateLimit);
		watch(roundFinished, () => {
			updateLimit();
			if (!audioPlaying.value) playAudio();
		});

		const musicInput = ref('');
		let dropdownOpen = false;

		const dropdownOpened = () => {
			dropdownOpen = true;
		};
		const dropdownClosed = (thing: any) => {
			if (thing.detail.selection)
				musicInput.value = thing.detail.selection.value.title;
			dropdownOpen = false;
		};

		const skip = () => {
			submitGuess();
			musicInput.value = '';
		};

		const submit = () => {
			if (dropdownOpen || !musicInput.value) return;
			submitGuess(musicInput.value);
			musicInput.value = '';
		};

		const previousTrack = () => {
			if (currentRound.value > 0) currentRound.value--;
		};

		const nextTrack = () => {
			if (currentRound.value < totalRounds.value - 1) currentRound.value++;
			else showShareScreen.value = true;
		};

		return {
			currentRound, totalRounds, firstRound,
			roundFinished, playerCorrect, playerCorrectTime,
			currentTrack, currentGame,
			previousTrack, nextTrack,
			secondSteps,
			endTime, endTimeDisplay,
			audioCurrentPosition, currentTimeDisplay,
			currentAttempt, nextAttemptTime,
			audioPlaying, togglePlay,
			guessList,
			formField,
			musicInput,
			dropdownOpened, dropdownClosed,
			skip, submit,
		};
	},
});
